import { of } from "rxjs";
import { catchError, filter, ignoreElements, map, mergeMap, tap } from "rxjs/operators";
import { StateObservable } from "redux-observable";

import { Effect } from "../../../../../redux/types/effect.type";
import { EffectActionsObservable } from "../../../../../redux/types/effect-actions-observable.type";
import { isActionType } from "../../../../../redux/action.functions";
import { addMessage } from "../../../../../redux/actions/messagesActions";

import { RootState } from "../../../../../redux/store";
import { FlyerDetailActions } from "./flyer-detail.actions";

import * as store from "./flyer-detail.store";
import * as actions from "./flyer-detail.actions";
import { FlyerDetailPageEffects } from "../pages/flyer-detail-page/store/flyer-detail-page.effects";
import * as flyerDetailService from "../services/flyer-detail.services";
import { LOCATION_CHANGE } from "connected-react-router";

import * as navigationService from "../../../../../features/navigation/services/navigation.service";
import { IFlyerDetailData } from "../../../types/flyer-detail-data/flyer-detail-data.interface";
import { Nullable } from "../../../../../types/common";
import * as flyerActions from "../../../store/flyer.actions";
import { createFlyerPermissions } from "../../../services/flyer-permission.service";
import * as userStore from "../../../../../features/user/store/user.store";
import { FlyerDetailAddPageEffects } from "../pages/flyer-detail-add-page/store/flyer-detail-add-page.effects";

const fetchFlyer: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.Fetch)),
    map((action) => action.payload),
    mergeMap((id: number) =>
      flyerDetailService.fetchFlyer(id).pipe(
        map((flyer) => actions.fetchSuccess(flyer)),
        catchError((error) => of(actions.fetchFailure(error)))
      )
    )
  );

const fetchFlyerSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.FetchSuccess)),
    map((action) => action.payload),
    map((flyer) => actions.updatePermissions(flyer))
  );

const deletePage: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.DeletePage)),
    map((action) => action.payload),
    mergeMap((pageId) =>
      flyerDetailService.deletePage(pageId).pipe(
        map(actions.deletePageSuccess),
        catchError((error) => of(actions.deletePageFailure(error)))
      )
    )
  );

const deletePageSuccess: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.DeletePageSuccess)),
    map(() => store.getFlyer(state$.value)),
    map((flyer) => actions.fetch(flyer!.id))
  );

const updatePermissions: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.UpdatePermissions)),
    map((action) => ({
      user: userStore.getUser(state$.value),
      flyer: action.payload,
    })),
    map(({ flyer, user }) => flyerActions.setPermissions(createFlyerPermissions(user, flyer)))
  );

const reset: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(LOCATION_CHANGE)),
    map(() => actions.resetState())
  );

const saveFlyer: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.Save)),
    map((action) => action.payload),
    mergeMap((payload) =>
      flyerDetailService.saveFlyer(payload).pipe(
        map((flyer) => actions.saveSuccess(flyer)),
        catchError((error) => of(actions.saveFailure(error)))
      )
    )
  );

const saveFlyerSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.SaveSuccess)),
    map(() => addMessage({ text: "Successfully saved design layout.", severity: "success" }))
  );

const saveFlyerFailure: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.SaveFailure)),
    map((action) => action.payload),
    map((error) => addMessage({ text: error.errorMessage, severity: "error" }))
  );

const changeName: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.ChangeName)),
    map((action) => ({
      name: action.payload,
      flyer: store.getFlyer(state$.value),
    })),
    mergeMap(({ name, flyer }) =>
      flyerDetailService.changeName(flyer!.id, name).pipe(
        map((flyer) => actions.saveSuccess(flyer)),
        catchError((error) => of(actions.saveFailure(error)))
      )
    )
  );

const addPage: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.AddPage)),
    map(() => store.getFlyer(state$.value)),
    tap((flyer) => navigationService.goToFlyerAddPage(flyer!.id)),
    ignoreElements()
  );

const addPageSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.AddPageSuccess)),
    map((action) => action.payload),
    map((page) => actions.goToPage(page.id))
  );

const goToPage: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerDetailActions.GoToPage)),
    map((action) => ({
      pageId: action.payload,
      flyer: store.getFlyer(state$.value),
    })),
    tap((data: { pageId: number; flyer: Nullable<IFlyerDetailData> }) =>
      navigationService.goToFlyerPage(data.flyer!.id, data.pageId)
    ),
    ignoreElements()
  );

export const FlyerDetailEffects = [
  fetchFlyer,
  fetchFlyerSuccess,
  reset,
  saveFlyer,
  saveFlyerSuccess,
  saveFlyerFailure,
  deletePage,
  deletePageSuccess,
  changeName,
  addPage,
  addPageSuccess,
  goToPage,
  updatePermissions,
  ...FlyerDetailPageEffects,
  ...FlyerDetailAddPageEffects,
];
